import { resolveDimOffsets, type DimPlacement } from "sketch/dim-stack";
import { footOnLine, otherTerminal, type PageBox } from "sketch/geometry";
import type { DimBinding, DimShape, DimTerminal } from "sketch/shapes/dim";
import { Vec, type Editor, type TLShapeId } from "tldraw";
import { invariant } from "ts-invariant";

import { boundAnchorPage } from "@/sketch/bound-point";

/** A dim's bindings keyed by terminal (at most one each, by identity). */
export function dimBindingsOf(
  editor: Editor,
  dimId: TLShapeId,
): Partial<Record<"start" | "end", DimBinding>> {
  const out: Partial<Record<"start" | "end", DimBinding>> = {};
  for (const binding of editor.getBindingsFromShape<DimBinding>(dimId, "dim")) {
    out[binding.props.terminal] = binding;
  }
  return out;
}

/** The point an endpoint is pinned to, before any edge is resolved. */
function dimRawEndpointPage(
  editor: Editor,
  shape: DimShape,
  terminal: DimTerminal,
): Vec {
  const binding = dimBindingsOf(editor, shape.id)[terminal];
  const pinned = binding && boundAnchorPage(editor, binding);
  return (
    pinned ??
    Vec.From(
      editor
        .getShapePageTransform(shape.id)
        .applyToPoint(shape.props[terminal]),
    )
  );
}

/**
 * A dim endpoint in page space, which is where it is really pinned: a bound
 * endpoint is its target's anchor (or the exact vertex it pins, so edits to
 * unrelated vertices cannot drag it), a free endpoint is the stored point
 * carried by the shape's own transform. An endpoint that measures to an edge
 * lands on the foot of the perpendicular from the other end, so a dim between
 * two faces measures across them however they run.
 */
export function dimEndpointPage(
  editor: Editor,
  shape: DimShape,
  terminal: DimTerminal,
): Vec {
  const binding = dimBindingsOf(editor, shape.id)[terminal];
  const edge = binding?.props.edge;
  if (!binding || !edge) return dimRawEndpointPage(editor, shape, terminal);
  // The edge's two ends resolve through the same helper every pinned end
  // uses; only the far one is named by the binding's `edge`.
  const near = boundAnchorPage(editor, binding);
  const far = boundAnchorPage(editor, { toId: binding.toId, props: edge });
  if (!near || !far) return dimRawEndpointPage(editor, shape, terminal);
  return Vec.From(
    footOnLine(
      dimRawEndpointPage(editor, shape, otherTerminal(terminal)),
      near,
      far,
    ),
  );
}

/**
 * The same endpoint in shape-local space, which is what the shape draws in.
 * Pulling the page point back through the shape's own transform is what
 * keeps a bound end pinned to its target on screen mid-drag, and what makes
 * a dim inside a group draw where its target is rather than where the
 * group's origin moved it.
 */
export function dimEndpointLocal(
  editor: Editor,
  shape: DimShape,
  terminal: "start" | "end",
): Vec {
  const page = dimEndpointPage(editor, shape, terminal);
  return Vec.From(
    editor.getShapePageTransform(shape.id).clone().invert().applyToPoint(page),
  );
}

/** What the dims stand clear of is the drawing itself: not annotation, not
 *  the unbounded reference lines of the grid, and not a PDF underlay, which
 *  is a sheet to trace over rather than anything the sheet is about. */
const NOT_DRAWING = new Set([
  "dim",
  "gridline",
  "text",
  "callout",
  "note",
  "image",
]);

/**
 * A dim's standoff. Placement is a layout over the whole page, not a per-dim
 * number, so it is resolved from every dim and everything they are drawn
 * against, authored offsets included: a string resolves as one group, and
 * that is what keeps a chain on one line whichever link was dragged. Each
 * read goes through tldraw's reactive graph, which re-derives it when the
 * plan moves.
 */
export function dimOffset(editor: Editor, shape: DimShape): number {
  const dims: DimPlacement[] = [];
  const drawing: PageBox[] = [];
  for (const other of editor.getCurrentPageShapes()) {
    if (other.type === "dim") {
      const dim = other as DimShape;
      dims.push({
        id: dim.id,
        direction: dim.props.direction,
        chain: dim.props.chain,
        offset: dim.props.offset,
        a: dimEndpointPage(editor, dim, "start"),
        b: dimEndpointPage(editor, dim, "end"),
      });
      continue;
    }
    if (NOT_DRAWING.has(other.type)) continue;
    // Leaves only: a container's bounds come from its children, which are in
    // this same list, so measuring one would ask this dim for the geometry
    // we are here to compute.
    if (editor.getSortedChildIdsForParent(other.id).length) continue;
    const bounds = editor.getShapePageBounds(other.id);
    if (bounds) {
      drawing.push({ x: bounds.x, y: bounds.y, w: bounds.w, h: bounds.h });
    }
  }
  const offset = resolveDimOffsets(dims, drawing).get(shape.id);
  invariant(offset !== undefined, `dim ${shape.id} is not on the current page`);
  return offset;
}

/** The other links of a dim's string. A chain is one line, so they resolve
 *  together and only one of them can carry the authored offset. */
export function chainSiblings(editor: Editor, shape: DimShape): DimShape[] {
  const { chain } = shape.props;
  if (!chain) return [];
  return editor
    .getCurrentPageShapes()
    .filter(
      (other): other is DimShape =>
        other.type === "dim" &&
        other.id !== shape.id &&
        (other as DimShape).props.chain === chain,
    );
}

export interface DimLayout {
  /** Measured points, local to the shape. */
  a: Vec;
  b: Vec;
  /** Dim line endpoints, local to the shape. */
  lineA: Vec;
  lineB: Vec;
  /** Label midpoint. */
  mid: Vec;
  /** Measured span in page units, per the dim's direction. */
  measured: number;
}

/** Lay out the dim line, extension lines, and label in shape-local space. */
export function dimLayout(editor: Editor, shape: DimShape): DimLayout {
  const a = dimEndpointLocal(editor, shape, "start");
  const b = dimEndpointLocal(editor, shape, "end");
  const { direction } = shape.props;
  const offset = dimOffset(editor, shape);

  if (direction === "x") {
    const lineY = Math.min(a.y, b.y) - offset;
    return {
      a,
      b,
      lineA: new Vec(a.x, lineY),
      lineB: new Vec(b.x, lineY),
      mid: new Vec((a.x + b.x) / 2, lineY),
      measured: Math.abs(b.x - a.x),
    };
  }
  if (direction === "y") {
    const lineX = Math.min(a.x, b.x) - offset;
    return {
      a,
      b,
      lineA: new Vec(lineX, a.y),
      lineB: new Vec(lineX, b.y),
      mid: new Vec(lineX, (a.y + b.y) / 2),
      measured: Math.abs(b.y - a.y),
    };
  }
  const delta = Vec.Sub(b, a);
  const length = delta.len() || 1;
  const normal = new Vec(-delta.y / length, delta.x / length).mul(-offset);
  const lineA = Vec.Add(a, normal);
  const lineB = Vec.Add(b, normal);
  return {
    a,
    b,
    lineA,
    lineB,
    mid: Vec.Med(lineA, lineB),
    measured: delta.len(),
  };
}

/**
 * The unit vector along which a dim's offset grows, matching dimLayout's
 * placement exactly: drag deltas project onto this to adjust the offset.
 */
export function offsetGrowthUnit(editor: Editor, shape: DimShape): Vec {
  const { direction } = shape.props;
  if (direction === "x") return new Vec(0, -1);
  if (direction === "y") return new Vec(-1, 0);
  const a = dimEndpointLocal(editor, shape, "start");
  const b = dimEndpointLocal(editor, shape, "end");
  const delta = Vec.Sub(b, a);
  const length = delta.len() || 1;
  return new Vec(delta.y / length, -delta.x / length);
}
